/**
 * GameContainer wraps individual game components and provides them with controller input context.
 * It manages game theme loading and passes focus navigation state to games via props.
 */
import React, { useMemo, useEffect, useCallback, useState } from 'react'
import { GameRegistry, GameComponent } from '../../../deps/catalog/gameRegistry/gameRegistry.types'
import { GameErrorBoundary } from './GameErrorBoundary'
import type { CrashReporter } from '../../../deps/debug/crashReporter/crashReporter.types'
import type { GameProps, PlayerConfiguration } from '@merels/sdk'
import { useTheme } from '../../../contexts/ThemeContext'
import { useFocusNavigationDep, useControllerInputDep } from '../../providers/FocusNavigationProvider/FocusNavigationProvider'
import { useControllerInputAdapter } from '@merels/input/hooks'
import { useSignals } from '@preact/signals-react/runtime'
import { Mode } from '../../../deps/mode/mode/mode.types'
import type { ControllerMates } from '../../../deps/localMates/controllerMates/controllerMates.types'
import type { PlayerAssignmentPreferences } from '../../../deps/matesToPlayers/playerAssignmentPreferences/playerAssignmentPreferences.types'
import { getEffectiveGameOptionsForStart } from '../../../deps/room/roomActions/modules/gameOptions.actions'
import type { GameSavedOptions } from '@merels/room'
import { getOrGenerateClientId } from '../../../deps/utils/clientId'
import { buildSourceToMateIdMap } from '@merels/game-ui-link'
import { makeRealtimeLoop } from '../../../deps/game/realtimeLoop/realtimeLoop'

interface GameContainerProps {
  gameId: string
  gameState: any
  ctx?: any
  /**
   * The published client version this game is pinned to (or was just
   * pushed), from room state — see @merels/room's GameCatalogClient.
   * Undefined for statically-bundled games.
   */
  gameVersion?: { versionId: string; clientBundleUrl: string; styleUrl?: string }
  playerToMateIds: string[][]
  viewingMateId?: string
  /**
   * The player slot index (0-based) that this client is viewing as.
   * Undefined in shared-screen mode or when not assigned to a player slot.
   */
  viewingPlayerIndex?: number
  applyMove: (move: any, mateId: string) => void
  /** Whether the active game started in a managed room. */
  isManagedRoom?: boolean
  /** Whether this client acts for a host mate. */
  viewerIsHost?: boolean
  /** Submits a host action for this client's host mate. */
  applyHostMove?: (move: any) => void
  /**
   * For a realtime game (see BgioGameLogic.realtime) in a networked room:
   * the live-ticking local sim's current state, owned and updated by
   * playAreaVM (see its "Realtime networked sim" section) — forwarded
   * straight into GameProps.getLiveGameState. Undefined when there's no
   * active realtime networked sim.
   */
  getLiveGameState?: () => unknown
  error?: string
  gameRegistry: GameRegistry
  mode: Mode
  controllerMates: ControllerMates
  playerAssignmentPreferences: PlayerAssignmentPreferences
  savedOptions: GameSavedOptions | null
  roomMates: Array<{ clientId: string; controllerId?: string; name: string; type: 'single' | 'shared' | 'test'; mateId?: string }>
  viewMode?: 'play' | 'rulebook'
  hintsEnabled?: boolean
  roomCode?: string
  crashReporter: CrashReporter
}

export function GameContainer({
  gameId,
  gameState,
  ctx,
  gameVersion,
  playerToMateIds,
  viewingMateId,
  viewingPlayerIndex,
  applyMove,
  isManagedRoom = false,
  viewerIsHost = false,
  applyHostMove,
  getLiveGameState,
  error,
  gameRegistry,
  mode,
  controllerMates,
  playerAssignmentPreferences,
  savedOptions,
  roomMates,
  viewMode = 'play',
  hintsEnabled = false,
  roomCode,
  crashReporter,
}: GameContainerProps) {
  const { setGameThemeInput, setPlayerPiece } = useTheme()
  useSignals() // Ensure signals are reactive - must be called before reading signal values
  const focusNavigation = useFocusNavigationDep()
  const controllerInput = useControllerInputDep()
  const controllerInputContext = useControllerInputAdapter(controllerInput)
  
  // Compute shared screen mode props - read signals reactively
  const isSharedScreen = mode.computedMode.value.sharedScreen
  // Networked, multi-client room (as opposed to local/shared-screen) — see
  // GameProps.isNetworked. Realtime games must not self-submit `tick`
  // moves in this mode (see realtimeLoop gating below).
  const isNetworked = !mode.computedMode.value.localMode
  const matesList = controllerMates.mates.value
  const localClientId = getOrGenerateClientId()
  
  // Compute which mates can make moves for the current player
  const currentPlayerMateIds = useMemo(() => {
    // Note: bgio uses string player IDs, and `'0'` is a valid currentPlayer.
    // Avoid truthiness checks here so we don't accidentally treat `'0'` as "missing".
    if (!isSharedScreen || typeof ctx?.currentPlayer === 'undefined' || !gameId) return undefined
    
    const currentBgioIndex = parseInt(ctx.currentPlayer)
    if (isNaN(currentBgioIndex)) return []

    // Continuous-simulation games (see BgioGameLogic.realtime) have no
    // "turn" — ctx.currentPlayer never advances, so every seat is always
    // active. Filtering to just the nominal current player would lock out
    // every seat but the first for the entire game.
    const isAlwaysActiveGame = Boolean(gameRegistry.getResolvedModule?.(gameId)?.gameLogic?.realtime)
    const idsForSlot = isAlwaysActiveGame ? playerToMateIds.flat() : playerToMateIds[currentBgioIndex] || []
    if (idsForSlot.length === 0) return []

    const localControllerIds = new Set(controllerMates.mates.value.map((m) => m.id))
    const result: string[] = []

    for (const pid of idsForSlot) {
      // Find the mate in the room roster to get their controllerId
      const mate = (roomMates as any[]).find((m) => m.mateId === pid)
      const deviceId = mate?.controllerId
      
      if (typeof deviceId === 'string' && localControllerIds.has(deviceId)) {
        result.push(deviceId)
      }
    }

    // If any of the mates in the slot are local to this client, allow keyboard input ('K')
    const hasLocalMateInSlot = idsForSlot.some(pid => {
      const mate = (roomMates as any[]).find(m => m.mateId === pid)
      return mate && mate.clientId === localClientId
    })
    
    if (hasLocalMateInSlot) {
      result.push('K')
    }

    return result
  }, [
    isSharedScreen,
    ctx?.currentPlayer,
    gameId,
    controllerMates.mates.value,
    playerToMateIds,
    roomMates,
    gameRegistry,
  ])

  const sharedScreenFallbackMateId = useMemo(() => {
    if (!isSharedScreen || typeof ctx?.currentPlayer === 'undefined' || !gameId) {
      return undefined
    }

    const currentBgioIndex = parseInt(ctx.currentPlayer)
    if (isNaN(currentBgioIndex)) {
      return undefined
    }

    const idsForSlot = playerToMateIds[currentBgioIndex] || []
    return idsForSlot.find((mateId) => {
      const mate = roomMates.find((roomMate) => roomMate.mateId === mateId)
      return mate?.clientId === localClientId
    })
  }, [
    isSharedScreen,
    ctx?.currentPlayer,
    gameId,
    playerToMateIds,
    roomMates,
    localClientId,
  ])
  
  // Update controller input filter when current player changes
  useEffect(() => {
    // Shared-screen policy:
    // - In content area: only allow input from the controllers assigned to the current player.
    // - Outside content area (header): allow all controllers.
    // - If currentPlayerMateIds is [] (remote player's turn), block all input.
    // - If currentPlayerMateIds is undefined (not shared mode), allow all input.
    if (isSharedScreen && focusNavigation.focusArea.value === 'content') {
      // Use the array directly - empty array blocks all, populated array allows specific devices
      const filter = currentPlayerMateIds ?? null
      controllerInput.setMateFilter(filter)
    } else {
      controllerInput.setMateFilter(null)
    }
    
    // Cleanup: clear filter when component unmounts or mode changes
    return () => {
      if (isSharedScreen) {
        controllerInput.setMateFilter(null)
      }
    }
  }, [
    isSharedScreen,
    currentPlayerMateIds,
    controllerInput,
    focusNavigation.focusArea.value,
    ctx?.currentPlayer,
    gameId,
  ])
  
  // Set data attribute on game wrapper to communicate focus navigation state to games
  const gameWrapperRef = React.useRef<HTMLDivElement>(null)
  const shouldEnableFocusNav = focusNavigation.isActive.value && focusNavigation.focusArea.value === 'content'
  const focusArea = focusNavigation.focusArea.value
  
  useEffect(() => {
    const element = gameWrapperRef.current
    if (element) {
      element.setAttribute('data-focus-navigation-active', String(shouldEnableFocusNav))
      element.setAttribute('data-focus-area', focusArea)
    }
  }, [shouldEnableFocusNav, focusArea])

  // Load assignments for this game when game starts (in shared screen mode)
  useEffect(() => {
    if (isSharedScreen && gameId) {
      const roomName = typeof window !== 'undefined' ? window.location.pathname.match(/^\/r\/([^\/]+)/)?.[1] : null
      if (roomName) {
        const actualRoomName = roomName.startsWith('shared:') ? roomName.substring(7) : roomName
        playerAssignmentPreferences.loadAssignments(actualRoomName, gameId)
      }
    }
  }, [isSharedScreen, gameId, playerAssignmentPreferences])
  
  // Some games load their client UI at runtime (see dynamicGameLoader) rather than
  // via a static import, so this can't be a synchronous useMemo for every game.
  const [GameComponent, setGameComponent] = useState<GameComponent | null>(null)
  const [RulebookComponent, setRulebookComponent] = useState<GameComponent | null>(null)

  // Loads both the component and its theme together, from a single source,
  // so they can never disagree about which version is showing.
  //
  // When room state carries a pinned/pushed gameVersion (see @merels/room's
  // GameCatalogClient), that's authoritative: every client in the room
  // loads exactly that version, and re-runs this effect when the room
  // pushes a new one (e.g. a live "preview" update) — not on any per-client
  // decision. Otherwise this falls back to the game's normal static/async
  // registry lookup. gameState/ctx keep flowing from room state untouched
  // by the remount, so the game position survives; only the swapped
  // component's own local UI state (focus, hover, etc.) resets, same as a
  // page reload would do.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (gameVersion && gameRegistry.loadGameComponentAtVersion) {
        const loaded = await gameRegistry.loadGameComponentAtVersion(gameId, gameVersion)
        if (cancelled) return
        setGameComponent(() => loaded?.Game ?? null)
        setRulebookComponent(() => loaded?.Rulebook ?? null)
        setGameThemeInput(loaded?.themeConfig ?? null)
        setPlayerPiece(loaded?.PlayerPiece ?? null)
        return
      }

      const component = gameRegistry.getGameComponentAsync
        ? await gameRegistry.getGameComponentAsync(gameId)
        : gameRegistry.getGameComponent(gameId)
      const themeConfig = gameRegistry.getGameThemeConfigAsync
        ? await gameRegistry.getGameThemeConfigAsync(gameId)
        : null
      if (cancelled) return
      setGameComponent(() => component)
      const rulebook = gameRegistry.getRulebookComponentAsync
        ? await gameRegistry.getRulebookComponentAsync(gameId)
        : null
      if (cancelled) return
      setRulebookComponent(() => rulebook)
      const playerPiece = gameRegistry.getPlayerPieceComponentAsync
        ? await gameRegistry.getPlayerPieceComponentAsync(gameId)
        : null
      if (cancelled) return
      setPlayerPiece(playerPiece)
      setGameThemeInput(themeConfig)
    }
    setGameComponent(null)
    setRulebookComponent(null)
    load()
    return () => {
      cancelled = true
      // Drop game theme / piece chrome when this game unmounts so Merels defaults return.
      setGameThemeInput(null)
      setPlayerPiece(null)
    }
  }, [gameId, gameVersion?.versionId, gameVersion?.clientBundleUrl, gameRegistry, setGameThemeInput, setPlayerPiece])

  // Compute effective game options for the current player count
  const playerCount = playerToMateIds.length || 2
  const gameOptions = useMemo(() => {
    return getEffectiveGameOptionsForStart(gameId, playerCount, savedOptions)
  }, [gameId, playerCount, savedOptions])
  
  const isGamePaused = mode.isGamePaused.value
  const togglePause = React.useCallback(() => {
    mode.isGamePaused.value = !mode.isGamePaused.value
  }, [mode])

  // Build source-to-mate mapping for action attribution
  const sourceToMateId = useMemo(() => buildSourceToMateIdMap({
    isSharedScreen,
    viewingMateId,
    sharedScreenFallbackMateId,
    localMates: matesList.map(m => ({ id: m.id })),
    roomMates: roomMates as Array<{ mateId?: string; controllerId?: string }>,
  }), [isSharedScreen, viewingMateId, sharedScreenFallbackMateId, matesList, roomMates])

  // Create submitMove function that maps sourceId to mateId
  const submitMove = useCallback((move: unknown, sourceId: string) => {
    const mateId = sourceToMateId.get(sourceId)
    if (!mateId) {
      console.warn(`[GameContainer] submitMove: unknown sourceId "${sourceId}"`, { sourceToMateId })
      return
    }
    applyMove(move, mateId)
  }, [sourceToMateId, applyMove])

  // Drives continuous-simulation games' fixed-rate `tick` move (see
  // BgioGameLogic.realtime). A no-op for ordinary turn-based games, which
  // don't declare `realtime`. Depends on GameComponent as a readiness gate:
  // getResolvedModule is populated by the same load() effect that sets
  // GameComponent, strictly before that state update, so re-running this
  // effect when GameComponent changes guarantees the module is resolved.
  const realtimeLoopRef = React.useRef<ReturnType<typeof makeRealtimeLoop> | null>(null)
  if (!realtimeLoopRef.current) {
    realtimeLoopRef.current = makeRealtimeLoop()
  }
  useEffect(() => {
    // Networked games run their own local simulation instead (see
    // networkedSim) — submitting `tick` as a room move here would have
    // every connected client log its own tick into the shared action log,
    // running the simulation at N times speed with non-deterministic
    // interleaving, and would explode replay cost (every client replays
    // the full action log on every broadcast).
    if (isNetworked) return
    const tickRateHz = gameRegistry.getResolvedModule?.(gameId)?.gameLogic?.realtime?.tickRateHz
    if (!tickRateHz) return
    const loop = realtimeLoopRef.current!
    loop.start(tickRateHz, () => submitMove({ type: 'tick', args: [] }, 'K'))
    return () => loop.stop()
  }, [gameId, gameVersion?.versionId, GameComponent, submitMove, gameRegistry, isNetworked])

  // Compute playerConfiguration for games that use the new API
  const playerConfiguration = useMemo((): PlayerConfiguration => {
    if (isSharedScreen) {
      return { mode: 'shared' }
    }

    // A race uses boardgame.io activePlayers so several seats can submit moves
    // concurrently. Other games continue to use ctx.currentPlayer.
    const currentPlayerIndex = ctx?.currentPlayer
    const activePlayers = ctx?.activePlayers as Record<string, unknown> | undefined
    const isMyTurn =
      typeof viewingPlayerIndex === 'number' &&
      (activePlayers
        ? Object.prototype.hasOwnProperty.call(activePlayers, String(viewingPlayerIndex))
        : typeof currentPlayerIndex !== 'undefined' && String(viewingPlayerIndex) === String(currentPlayerIndex))

    return { mode: 'single', isMyTurn }
  }, [isSharedScreen, ctx?.currentPlayer, ctx?.activePlayers, viewingPlayerIndex])

  const ActiveComponent = viewMode === 'rulebook' ? RulebookComponent : GameComponent
  if (!ActiveComponent) {
    if (viewMode === 'rulebook') return <div>Rulebook unavailable for: {gameId}</div>
    return <div>Loading game: {gameId}...</div>
  }

  const props: GameProps = {
    gameState,
    ctx,
    playerConfiguration,
    submitMove,
    error,
    controllerInputContext,
    enableFocusNavigation: shouldEnableFocusNav,
    hintsEnabled,
    focusArea: focusArea === 'header' || focusArea === 'content' ? focusArea : undefined,
    gameOptions,
    isGamePaused,
    onTogglePause: togglePause,
    currentPlayerParticipantIds: currentPlayerMateIds,
    viewingPlayerIndex,
    isNetworked,
    getLiveGameState,
    isManagedRoom,
    viewerIsHost,
    submitHostMove: isManagedRoom && viewerIsHost ? applyHostMove : undefined,
  }

  return (
    <div ref={gameWrapperRef} className="game-component-wrapper">
      <GameErrorBoundary
        gameId={gameId}
        versionId={gameVersion?.versionId}
        roomCode={roomCode}
        viewingPlayerIndex={viewingPlayerIndex}
        viewingMateId={viewingMateId}
        playerToMateIds={playerToMateIds}
        isSharedScreen={isSharedScreen}
        crashReporter={crashReporter}
      >
        <ActiveComponent {...props} />
      </GameErrorBoundary>
    </div>
  )
}
